import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { focusAfterRemoval } from '@/hooks/focus-after-removal';

interface KvEditorProps {
  value: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
  /** True while a key is blank or two rows share a key. The form uses this to keep Save disabled. */
  onDuplicateChange?: (hasDuplicate: boolean) => void;
  label?: string;
  disabled?: boolean;
  /** Owner named in each row's remove button, e.g. "endpoint createPayment". */
  ownerLabel?: string;
}

interface KvRow {
  id: string;
  key: string;
  value: string;
  /** False until the key is edited or blurred, so a new row does not open with an error. */
  touched: boolean;
}

const rowsFromValue = (value: Record<string, string>): KvRow[] =>
  Object.entries(value).map(([key, rowValue]) => ({
    id: crypto.randomUUID(),
    key,
    value: rowValue,
    touched: true,
  }));

/**
 * A record cannot hold two entries with the same key. Returning null leaves the
 * rows in local state so a keystroke that passes through an existing key does
 * not drop the other row's value. A blank key also returns null so the previous
 * record stays in parent state until the row has a key again.
 */
const recordFromRows = (rows: readonly KvRow[]): Record<string, string> | null => {
  const seen = new Set<string>();
  const next: Record<string, string> = {};
  for (const row of rows) {
    if (!row.key.trim()) return null;
    if (seen.has(row.key)) return null;
    seen.add(row.key);
    next[row.key] = row.value;
  }
  return next;
};

const duplicateRowIds = (rows: readonly KvRow[]): Set<string> => {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.key, (counts.get(row.key) ?? 0) + 1);
  return new Set(
    rows.filter((row) => row.key && (counts.get(row.key) ?? 0) > 1).map((row) => row.id)
  );
};

export const KvEditor: React.FC<KvEditorProps> = ({
  value,
  onChange,
  onDuplicateChange,
  label,
  disabled,
  ownerLabel,
}) => {
  const [rows, setRows] = useState(() => rowsFromValue(value));
  // The last record pushed to the parent, or the record the parent last pushed
  // in. Matching JSON means the prop echo is our own edit, so row ids stay put.
  const lastEmitted = useRef(JSON.stringify(value));
  const onChangeRef = useRef(onChange);
  const onDuplicateChangeRef = useRef(onDuplicateChange);
  const rootRef = useRef<HTMLDivElement>(null);
  onChangeRef.current = onChange;
  onDuplicateChangeRef.current = onDuplicateChange;

  const duplicates = duplicateRowIds(rows);
  const hasDuplicate = duplicates.size > 0;
  const hasBlank = rows.some((row) => row.key.trim() === '');
  const invalid = hasDuplicate || hasBlank;

  useEffect(() => {
    const incoming = JSON.stringify(value);
    if (incoming === lastEmitted.current) return;
    lastEmitted.current = incoming;
    setRows(rowsFromValue(value));
  }, [value]);

  useEffect(() => {
    onDuplicateChangeRef.current?.(invalid);
    if (invalid) return;

    const record = recordFromRows(rows);
    if (!record) return;
    const serialized = JSON.stringify(record);
    if (serialized === lastEmitted.current) return;
    lastEmitted.current = serialized;
    onChangeRef.current(record);
  }, [rows, invalid]);

  useEffect(() => {
    return () => onDuplicateChangeRef.current?.(false);
  }, []);

  const removeRow = (id: string) => {
    const ids = rows.map((row) => row.id);
    focusAfterRemoval(rootRef.current, ids, ids.indexOf(id), () =>
      setRows((current) => current.filter((row) => row.id !== id))
    );
  };

  return (
    <div ref={rootRef} className="space-y-2">
      {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
      {rows.length > 0 && (
        <div className="space-y-1.5">
          {rows.map((row, index) => {
            const blank = row.key.trim() === '';
            const required = blank && row.touched;
            const duplicate = duplicates.has(row.id);
            const errorId = `${row.id}-error`;
            const entryName = blank ? String(index + 1) : row.key;
            return (
              <div key={row.id} className="flex items-start gap-1.5">
                <div className="min-w-0 flex-1 space-y-1">
                  <Input
                    className="h-7 text-xs font-mono"
                    disabled={disabled}
                    value={row.key}
                    onChange={(e) =>
                      setRows((current) =>
                        current.map((item) =>
                          item.id === row.id
                            ? { ...item, key: e.target.value, touched: true }
                            : item
                        )
                      )
                    }
                    onBlur={() => {
                      if (row.touched) return;
                      setRows((current) =>
                        current.map((item) =>
                          item.id === row.id ? { ...item, touched: true } : item
                        )
                      );
                    }}
                    placeholder="key"
                    aria-invalid={required || duplicate || undefined}
                    aria-describedby={required || duplicate ? errorId : undefined}
                  />
                  <p
                    id={errorId}
                    data-error-slot=""
                    className="min-h-[1.5em] text-[11px] text-destructive"
                  >
                    {required ? 'Key is required' : duplicate ? 'Duplicate key' : ''}
                  </p>
                </div>
                <span className="text-muted-foreground text-xs shrink-0 pt-1.5">:</span>
                <Input
                  className="h-7 text-xs font-mono flex-1"
                  disabled={disabled}
                  value={row.value}
                  onChange={(e) =>
                    setRows((current) =>
                      current.map((item) =>
                        item.id === row.id ? { ...item, value: e.target.value } : item
                      )
                    )
                  }
                  placeholder="value"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={disabled}
                  aria-label={
                    ownerLabel
                      ? `Remove entry ${entryName} from ${ownerLabel}`
                      : `Remove entry ${entryName}`
                  }
                  data-remove-row={row.id}
                  onClick={() => removeRow(row.id)}
                >
                  <IconTrash size={13} aria-hidden />
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs gap-1"
        disabled={disabled}
        data-add-row=""
        onClick={() =>
          setRows((current) => [
            ...current,
            { id: crypto.randomUUID(), key: '', value: '', touched: false },
          ])
        }
      >
        <IconPlus size={12} />
        Add entry
      </Button>
    </div>
  );
};
